import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { api } from '../api';
import { STATIC } from '../staticMode';
import { glossStore, glossText } from '../staticStore';
import { entryKey } from '../utils/wordText';

const LOCAL_KEY = 'seediq_gloss';

// Backend-backed custom meanings (per user). Loads the whole map into state so
// getGloss stays synchronous; writes persist to the server and mirror to
// localStorage as an offline backup. Existing browser glosses migrate up once.
export function useGloss() {
  const { token } = useAuth();
  const [glosses, setGlosses] = useState(() => (STATIC ? glossStore.getAll() : {}));

  useEffect(() => {
    if (STATIC) return;                          // static: personal glosses from localStorage (above)
    if (!token) { setGlosses({}); return; }
    let alive = true;
    api('/glosses', { token }).then(server => {
      const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
      // Merge: this device's edits win, so a save that failed to reach the
      // server is never wiped — and we re-push so the server catches up.
      const merged = { ...server, ...local };
      if (Object.keys(local).length) {
        api('/glosses/bulk', { method: 'POST', token, body: merged }).catch(() => {});
      }
      if (alive) {
        setGlosses(merged);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(merged));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [token]);

  // Keyed "<dialect>:<word>", exactly like vocab — see entryKey. The argument
  // order matches useVocab's getStatus/setStatus deliberately: the two are
  // called side by side in WordPopup, and a swapped pair would quietly file a
  // correction under a language named after a word.
  //
  // The bare-word fallback reads anything written before the key carried a
  // language. There is provably none in the cloud — the deployed build shipped
  // no gloss editor — but a dev browser or the dev backend may hold some, and a
  // correction is not the kind of thing to drop for tidiness. Reads fall back;
  // writes are always language-keyed, so the old shape dies out on contact.
  const getGloss = useCallback((dialect, word) => {
    if (!word) return null;
    return glossText(glosses[entryKey(dialect, word)] ?? glosses[word.toLowerCase()]);
  }, [glosses]);

  // opts.was — what the dictionary was showing when they overwrote it, so a
  // correction can be told from a fill-in. Only the FIRST write records it: on
  // a second edit the dictionary's original is still the interesting baseline,
  // not the reviewer's own previous attempt.
  // opts.note — why, in their words.
  const setGloss = useCallback((dialect, word, gloss, opts = {}) => {
    if (!word) return;
    const key = entryKey(dialect, word);

    // Built HERE, not inside the setGlosses updater: an updater runs at render
    // time, so a record assigned inside it is still null on the line below that
    // persists it — which would quietly store nothing and delete the gloss.
    let record = null;
    if (gloss !== null && gloss !== '') {
      const old = glosses[key];
      const hadWas = old && typeof old === 'object' && old.was !== undefined;
      record = {
        gloss,
        was: hadWas ? old.was : (opts.was ?? null),
        at: new Date().toISOString(),
      };
      if (opts.note) record.note = opts.note;
    }

    setGlosses(prev => {
      const next = { ...prev };
      if (record) next[key] = record; else delete next[key];
      delete next[word.toLowerCase()];   // retire the language-blind entry it replaces
      if (!STATIC) localStorage.setItem(LOCAL_KEY, JSON.stringify(next));  // dev: offline backup
      return next;
    });
    if (STATIC) { glossStore.set(key, record); return; }
    // The dev backend's column is a string; the record stays local there.
    if (token) api('/glosses', { method: 'PUT', token, body: { word: key, gloss: gloss || '' } }).catch(() => {});
  }, [token, glosses]);

  return { getGloss, setGloss };
}
